import React, { useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import { POLICY_CONFIG_DECISION_COMPONENT_RESULT_OPTIONS } from "@constants/policy-config-decision-component-result-options";
import { CONDITION_TRIGGER_OPTIONS } from "@constants/condition-trigger-options";
import { resources } from "@constants/hardcoded-strings";
import { POLICY_CONFIG_DECISION_ATTRIBUTE_FIELD_RESULT_OPTIONS } from "@constants/policy-config-decision-attribute-field-result-options";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { MultiSelectFormGroup } from "@containers/common/multi-select-form-group/multi-select-form-group";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group"
import { AttributeClassificationTypeIds } from "@enums/attribute-classification-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { PolicyConfigDecisionAttributeFieldResultTypes } from "@enums/policy-config-decision-attribute-field-result-type";
import { TriggerTypes } from "@enums/trigger-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import { CreatePolicyComponentTriggerConfigurationConditionValidationErrors } from "@idw-types/common/validation/create-policy-component/create-policy-component-validation-error";
import { AxnAttributeType, AxnAttributeTypeField } from "@idw-types/data/reference-data/axn";
import { AvailableAssertionStatus, DeployedPolicyComponent } from "@idw-types/data/reference-data/generic";
import { FormattedPolicyComponentCondition } from "@idw-types/data/reference-data/generic/policy-component";
import { AxnAcquiredAttribute } from "@idw-types/data/reference-data/axn/axn-acquired-attribute";
import {
    FormattedRpServiceConfigurationServiceOffering,
    FormattedRpServiceConfigurationServiceOfferingAssertion
} from "@idw-types/data/rp-service-configuration";
import { sortStrings } from "@utils/string-util";
import styles from "./create-policy-component-condition-container.less";

type CreatePolicyComponentConditionContainerProps = {
    index: number;
    isDisabled: boolean;
    condition: FormattedPolicyComponentCondition;
    conditions: Array<FormattedPolicyComponentCondition>;
    allAxnAttributeTypes: Array<AxnAttributeType>;
    axnAttributeTypes: Array<AxnAttributeType>;
    allAxnAttributeTypeFields: Array<AxnAttributeTypeField>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    axnAcquiredAttributeTypeFields: Array<AxnAttributeTypeField>;
    allPolicyComponents: Array<DeployedPolicyComponent>;
    policyComponents: Array<DeployedPolicyComponent>;
    validationErrors: CreatePolicyComponentTriggerConfigurationConditionValidationErrors;
    assertions: Array<FormattedRpServiceConfigurationServiceOfferingAssertion>;
    availableAssertions: Array<FormattedRpServiceConfigurationServiceOfferingAssertion>;
    serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>;
    acquiredAttributes: Array<AxnAcquiredAttribute>;
    axnAvailableAssertionStatuses: Array<AvailableAssertionStatus>;
    removeCondition: (uuid: string) => void;
    setCondition: (condition: FormattedPolicyComponentCondition) => void;
}

export const CreatePolicyComponentConditionContainer = (props: CreatePolicyComponentConditionContainerProps) => {
    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const [layout] = useState(LayoutTypes.ROW);
    const triggerConfigurationResources = resources.modals.createPolicyComponent.steps.triggerConfiguration;
    const {
        index,
        isDisabled,
        condition,
        allAxnAttributeTypes,
        axnAttributeTypes,
        allAxnAttributeTypeFields,
        axnAttributeTypeFields,
        axnAcquiredAttributeTypeFields,
        allPolicyComponents,
        policyComponents,
        conditions,
        validationErrors,
        assertions,
        availableAssertions,
        serviceOfferings,
        acquiredAttributes,
        axnAvailableAssertionStatuses,
        removeCondition,
        setCondition
    } = props;

    const handlePolicyComponentTriggerChange = (selectedOption: SelectOption<TriggerTypes>) => {
        let policyConditionOperator = condition.policyConditionOperator;

        if (selectedOption.value !== condition.triggerType) {
            policyConditionOperator = PolicyConfigDecisionAttributeFieldResultTypes.EQUAL_TO;
        }

        setCondition({
            ...condition,
            assertionID: null,
            serviceOfferingID: null,
            attributeTypeID: null,
            attributeFieldID: null,
            values: null,
            result: null,
            componentID: null,
            triggerType: selectedOption.value,
            policyConditionOperator: policyConditionOperator
        });
    };

    const handlePolicyComponentServiceOfferingChange = (selectedOption: SelectOption<string>) => {
        setCondition({
            ...condition,
            serviceOfferingID: selectedOption.value,
            assertionID: null,
            attributeFieldID: null,
            attributeTypeID: null,
            values: null
        });
    };

    const handlePolicyComponentAssertionChange = (selectedOption: SelectOption<string>) => {
        setCondition({
            ...condition,
            assertionID: selectedOption.value,
            attributeFieldID: null,
            attributeTypeID: null,
            values: null
        });
    };

    const handlePolicyComponentAttributeTypeChange = (selectedOption: SelectOption<string>) => {
        let attributeFieldID = null;
        let values = null;
        if (selectedOption) {
            const attrFields = axnAttributeTypeFields.filter(typeField => typeField.attributeTypeID === selectedOption.value);
            if (attrFields.length === 1) {
                attributeFieldID = attrFields[0].id;

                if (attrFields[0].defaultValue) {
                    values = attrFields[0].defaultValue;
                }
            }
        }

        setCondition({
            ...condition,
            attributeTypeID: selectedOption.value,
            attributeFieldID: attributeFieldID,
            assertionID: null,
            serviceOfferingID: null,
            values
        });
    };

    const handlePolicyComponentAttributeFieldChange = (selectedOption: SelectOption<string>) => {
        const attributeField = axnAttributeTypeFields.find(atf => atf.id === selectedOption.value);
        let values = null;

        if (attributeField?.defaultValue) {
            values = attributeField.defaultValue;
        }

        setCondition({
            ...condition,
            attributeFieldID: selectedOption.value,
            assertionID: null,
            serviceOfferingID: null,
            values
        });
    };

    const handlePolicyComponentAcquiredAttributeTypeChange = (selectedOption: SelectOption<string>) => {
        let attributeFieldID = null;
        let values = null;
        if (selectedOption) {
            let filteredAcquiredAttributeTypeFields = axnAcquiredAttributeTypeFields;

            if (condition.serviceOfferingID) {
                filteredAcquiredAttributeTypeFields = filteredAcquiredAttributeTypeFields.filter((attributeField => {
                    return !!acquiredAttributes.find(aa => aa.attributeTypeID === attributeField.attributeTypeID && condition.serviceOfferingID === aa.serviceOfferingID);
                }));
            }
            filteredAcquiredAttributeTypeFields = filteredAcquiredAttributeTypeFields.filter((attributeField => {
                return attributeField.attributeTypeID === selectedOption.value;
            }));

            if (filteredAcquiredAttributeTypeFields.length === 1) {
                attributeFieldID = filteredAcquiredAttributeTypeFields[0].id;

                if (filteredAcquiredAttributeTypeFields[0].defaultValue) {
                    values = filteredAcquiredAttributeTypeFields[0].defaultValue;
                }
            }
        }

        setCondition({
            ...condition,
            assertionID: null,
            attributeFieldID,
            attributeTypeID: selectedOption.value,
            values
        });
    };

    const handlePolicyComponentAcquiredAttributeFieldChange = (selectedOption: SelectOption<string>) => {
        const attributeField = axnAcquiredAttributeTypeFields.find(atf => atf.id === selectedOption.value);
        let values = null;

        if (attributeField?.defaultValue) {
            values = attributeField.defaultValue;
        }

        setCondition({
            ...condition,
            assertionID: null,
            attributeFieldID: selectedOption.value,
            values
        });
    };

    const handlePolicyComponentAssertionStatusChange = (selectedOptions: Array<SelectOption<string>>) => {
        setCondition({
            ...condition,
            values: selectedOptions.map(opt => opt.value).join(",")
        });
    };

    const handleComparisonChange = (selectedOption: SelectOption<PolicyConfigDecisionAttributeFieldResultTypes>) => {
        setCondition({
            ...condition,
            policyConditionOperator: selectedOption.value
        });
    }

    const handlePolicyComponentValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCondition({
            ...condition,
            values: event.target.value
        });
    }

    const handlePolicyComponentChange = (selectedOption: SelectOption<string>) => {
        setCondition({
            ...condition,
            componentID: selectedOption.value,
            assertionID: null,
            serviceOfferingID: null,
            attributeFieldID: null,
            values: null,
            result: null
        });
    }

    const handlePolicyComponentStatusChange = (selectedOption: SelectOption<string>) => {
        setCondition({
            ...condition,
            values: selectedOption.value
        });
    }

    const removeTrigger = () => {
        removeCondition(condition.uuid);
    }

    const selectedTriggerOption = useMemo(() => {
        if (condition.triggerType) {
            return CONDITION_TRIGGER_OPTIONS.find(option => option.value === condition.triggerType);
        }

        return null;
    }, [condition.triggerType]);

    const serviceOfferingOptions: Array<SelectOption> = useMemo(() => {
        let filteredServiceOfferings = serviceOfferings.filter(so => so.addedToService && so.status === EntityStatusTypes.ACTIVE);
        if (condition.triggerType === TriggerTypes.ACQUIRED_DATA) {
            filteredServiceOfferings = filteredServiceOfferings.filter(so => {
                const filteredAcquiredAttributeTypeFields = axnAcquiredAttributeTypeFields.filter((attributeField => {
                    return !!acquiredAttributes.find(aa => aa.attributeTypeID === attributeField.attributeTypeID && so.serviceOfferingID === aa.serviceOfferingID);
                }));

                return filteredAcquiredAttributeTypeFields.length > 0;
            });
        }

        return filteredServiceOfferings
            .map(so => ({ label: so.name, value: so.serviceOfferingID }))
            .sort((a, b) => sortStrings(a.label, b.label));
    }, [serviceOfferings, axnAcquiredAttributeTypeFields, acquiredAttributes, condition.triggerType]);

    const selectedServiceOfferingOption: SelectOption = useMemo(() => {
        if ([TriggerTypes.ASSERTION, TriggerTypes.ACQUIRED_DATA].includes(condition.triggerType) && condition.serviceOfferingID) {
            const foundSO = serviceOfferings.find(serviceOffering => serviceOffering.serviceOfferingID === condition.serviceOfferingID);
            if (foundSO) {
                return {
                    label: foundSO.name,
                    value: foundSO.serviceOfferingID
                };
            }
        }

        return null;
    }, [condition.triggerType, serviceOfferings, condition.assertionID, condition.serviceOfferingID]);

    const assertionOptions: Array<SelectOption> = useMemo(() => {
        if (selectedServiceOfferingOption) {
            return availableAssertions
                .filter(assertion => assertion.serviceOfferingID === selectedServiceOfferingOption.value)
                .map(assertion => ({ label: assertion.shortName, value: assertion.assertionId }))
                .sort((a, b) => sortStrings(a.label, b.label));
        } else {
            const soIds = serviceOfferingOptions.map(so => so.value);
            return availableAssertions
                .filter(assertion => soIds.includes(assertion.serviceOfferingID))
                .map(assertion => ({ label: assertion.shortName, value: assertion.assertionId }))
                .sort((a, b) => sortStrings(a.label, b.label));
        }
    }, [selectedServiceOfferingOption, serviceOfferingOptions, assertions, availableAssertions]);

    const selectedAssertionOption: SelectOption = useMemo(() => {
        if (condition.triggerType === TriggerTypes.ASSERTION && condition.assertionID && condition.serviceOfferingID) {
            const foundAssertion = assertions.find(assertion => assertion.assertionId === condition.assertionID && assertion.serviceOfferingID === condition.serviceOfferingID);
            if (foundAssertion) {
                return {
                    label: foundAssertion.shortName,
                    value: foundAssertion.assertionId
                };
            }
        }

        return null;
    }, [condition.triggerType, assertions, condition.assertionID]);

    const assertionStatusOptions: Array<SelectOption<string>> = useMemo(() => {
        return axnAvailableAssertionStatuses.map(assertionStatus => ({
            label: assertionStatus.label,
            value: assertionStatus.name
        }))
    }, [axnAvailableAssertionStatuses]);

    const selectedAssertionStatusOptions = useMemo(() => {
        if (condition.triggerType === TriggerTypes.ASSERTION && condition.values) {
            const statuses = condition.values.split(",").filter(status => status).map(status => status.trim());
            return assertionStatusOptions.filter(option => statuses.includes(option.value));
        }

        return [];
    }, [condition.triggerType, assertionStatusOptions, condition.values]);

    const attributeTypeOptions = useMemo(() => {
        const availableAttributes = axnAttributeTypes.filter(attr => [
            AttributeClassificationTypeIds.PII,
            AttributeClassificationTypeIds.DEVICES,
            AttributeClassificationTypeIds.PREFERENCES,
            AttributeClassificationTypeIds.ORGANIZATION,
            AttributeClassificationTypeIds.OTHER,
            AttributeClassificationTypeIds.GOVERNMENT_ISSUED,
            AttributeClassificationTypeIds.CUSTOM
        ].includes(attr.attributeClassificationID));

        const filteredAttributeTypeOptions = availableAttributes.map(attributeType => ({
            label: attributeType.name,
            value: attributeType.id
        }));
        return filteredAttributeTypeOptions.sort((a, b) => sortStrings(a.label, b.label));
    }, [axnAttributeTypes, axnAttributeTypeFields]);

    const selectedAttributeTypeOption = useMemo(() => {
        if (condition.attributeTypeID) {
            const foundAttributeType = allAxnAttributeTypes.find(attribute => attribute.id === condition.attributeTypeID);
            if (foundAttributeType) {
                return {
                    label: foundAttributeType.name,
                    value: foundAttributeType.id
                };
            }
        }

        return null;
    }, [condition.triggerType, allAxnAttributeTypes, condition.attributeTypeID]);

    const attributeFieldOptions = useMemo(() => {
        let filteredAttributeFields = axnAttributeTypeFields;
        if (condition.attributeTypeID) {
            filteredAttributeFields = filteredAttributeFields.filter(typeField => typeField.attributeTypeID === condition.attributeTypeID);
        }
        const filteredAttributeFieldOptions = filteredAttributeFields.map(attributeField => ({
            label: attributeField.label,
            value: attributeField.id
        }));
        return filteredAttributeFieldOptions.sort((a, b) => sortStrings(a.label, b.label));
    }, [axnAttributeTypeFields, condition.attributeTypeID]);

    const selectedAttributeFieldOption = useMemo(() => {
        if (condition.attributeFieldID) {
            const foundAttribute = allAxnAttributeTypeFields.find(attribute => attribute.id === condition.attributeFieldID);
            if (foundAttribute) {
                return {
                    label: foundAttribute.label,
                    value: foundAttribute.id
                };
            }
        }

        return null;
    }, [condition.triggerType, allAxnAttributeTypeFields, condition.attributeFieldID]);

    const selectedComparisonOption = useMemo(() => {
        return POLICY_CONFIG_DECISION_ATTRIBUTE_FIELD_RESULT_OPTIONS.find(option => option.value === condition.policyConditionOperator);
    }, [condition.policyConditionOperator]);

    const acquiredAttributeTypeOptions = useMemo(() => {

        if (condition.serviceOfferingID) {
            const acquiredAttributeFields = axnAcquiredAttributeTypeFields.filter((attributeField => {
                return !!acquiredAttributes.find(aa => aa.attributeTypeID === attributeField.attributeTypeID && condition.serviceOfferingID === aa.serviceOfferingID);
            }));

            const attributeTypeIds = new Set(acquiredAttributeFields.map(field => field.attributeTypeID));
            const acquiredAttributeTypes = allAxnAttributeTypes.filter(type => attributeTypeIds.has(type.id));

            const filteredAcquiredAttributeTypeOptions = acquiredAttributeTypes.map(attributeType => ({
                label: attributeType.name,
                value: attributeType.id
            }));

            return filteredAcquiredAttributeTypeOptions.sort((a, b) => sortStrings(a.label, b.label));
        }
        return [];
    }, [axnAcquiredAttributeTypeFields, acquiredAttributes, condition.serviceOfferingID]);

    const acquiredAttributeFieldOptions = useMemo(() => {
        let filteredAcquiredAttributeTypeFields = axnAcquiredAttributeTypeFields;

        if (condition.serviceOfferingID) {
            filteredAcquiredAttributeTypeFields = filteredAcquiredAttributeTypeFields.filter((attributeField => {
                return !!acquiredAttributes.find(aa => aa.attributeTypeID === attributeField.attributeTypeID && condition.serviceOfferingID === aa.serviceOfferingID);
            }));
        }
        if (condition.attributeTypeID) {
            filteredAcquiredAttributeTypeFields = filteredAcquiredAttributeTypeFields.filter((attributeField => {
                return attributeField.attributeTypeID === condition.attributeTypeID;
            }));
        }

        const filteredAttributeFieldOptions = filteredAcquiredAttributeTypeFields.map(attributeField => ({
            label: attributeField.label,
            value: attributeField.id
        }));

        return filteredAttributeFieldOptions.sort((a, b) => sortStrings(a.label, b.label));
    }, [axnAcquiredAttributeTypeFields, acquiredAttributes, condition.attributeTypeID, condition.serviceOfferingID]);

    const policyComponentOptions = useMemo(() => {
        return policyComponents.reduce((filteredPolicyComponents, policyComponent) => {
            filteredPolicyComponents.push({
                label: policyComponent.name,
                value: policyComponent.id
            });
            return filteredPolicyComponents;
        }, []).sort((a, b) => sortStrings(a.label, b.label));
    }, [policyComponents, conditions]);

    const selectedPolicyComponentOption = useMemo(() => {
        if (condition.triggerType === TriggerTypes.COMPONENT && condition.componentID) {
            const foundComponent = allPolicyComponents.find(pc => pc.id === condition.componentID);
            if (foundComponent) {
                return {
                    label: foundComponent.name,
                    value: foundComponent.id
                };
            }
        }

        return null;
    }, [condition.triggerType, allPolicyComponents, condition.componentID]);

    const selectedPolicyComponentStatusOption = useMemo(() => {
        if (condition.triggerType === TriggerTypes.COMPONENT && condition.values) {
            const firstChoice = POLICY_CONFIG_DECISION_COMPONENT_RESULT_OPTIONS.find(option => option.value === condition.values);
            if (firstChoice) {
                return firstChoice
            } else if (condition.values === "0") {
                return POLICY_CONFIG_DECISION_COMPONENT_RESULT_OPTIONS[1];
            } else if (condition.values === "1") {
                return POLICY_CONFIG_DECISION_COMPONENT_RESULT_OPTIONS[0];
            }
        }

        return null;
    }, [
        condition.triggerType,
        POLICY_CONFIG_DECISION_COMPONENT_RESULT_OPTIONS,
        condition.values
    ]);

    return (
        <div className={styles.createPolicyComponentConditionContainer}>

            <SelectFormGroup
                automationId={`creation-policy-component-trigger-configuration-trigger-select-${index}`}
                controlId={`creation-policy-component-trigger-configuration-trigger-select-${index}`}
                label={triggerConfigurationResources.inputs.trigger.label}
                ariaLabel={triggerConfigurationResources.inputs.trigger.label}
                onChange={handlePolicyComponentTriggerChange}
                options={CONDITION_TRIGGER_OPTIONS}
                selectedOption={selectedTriggerOption}
                disabled={isDisabled}
                isRequired={true}
                isSearchable={false}
                isClearable={false}
                labelWidth={labelWidth}
                layout={layout}
            />

            {condition.triggerType === TriggerTypes.ASSERTION &&
                <>
                    <SelectFormGroup
                        automationId={`creation-policy-component-trigger-configuration-so-select-${index}`}
                        controlId={`creation-policy-component-trigger-configuration-so-select-${index}`}
                        label={triggerConfigurationResources.inputs.serviceOffering.label}
                        ariaLabel={triggerConfigurationResources.inputs.serviceOffering.label}
                        onChange={handlePolicyComponentServiceOfferingChange}
                        options={serviceOfferingOptions}
                        selectedOption={selectedServiceOfferingOption}
                        disabled={isDisabled}
                        isRequired={true}
                        isSearchable={true}
                        isClearable={false}
                        labelWidth={labelWidth}
                        layout={layout}
                    />
                    {validationErrors.serviceOfferingError}

                    <SelectFormGroup
                        automationId={`creation-policy-component-trigger-configuration-assertion-select-${index}`}
                        controlId={`creation-policy-component-trigger-configuration-assertion-select-${index}`}
                        label={triggerConfigurationResources.inputs.assertion.label}
                        ariaLabel={triggerConfigurationResources.inputs.assertion.label}
                        onChange={handlePolicyComponentAssertionChange}
                        options={assertionOptions}
                        selectedOption={selectedAssertionOption}
                        disabled={isDisabled || !condition.serviceOfferingID}
                        isRequired={true}
                        isSearchable={true}
                        isClearable={false}
                        labelWidth={labelWidth}
                        layout={layout}
                    />
                    {validationErrors.assertionError}

                    <MultiSelectFormGroup
                        automationId={`creation-policy-component-trigger-configuration-assertion-status-select-${index}`}
                        controlId={`creation-policy-component-trigger-configuration-assertion-status-select-${index}`}
                        label={triggerConfigurationResources.inputs.assertionStatus.label}
                        ariaLabel={triggerConfigurationResources.inputs.assertionStatus.label}
                        keepMenuOpenOnSelect={true}
                        onChangeMulti={handlePolicyComponentAssertionStatusChange}
                        options={assertionStatusOptions}
                        selectedOptions={selectedAssertionStatusOptions}
                        disabled={isDisabled}
                        isRequired={true}
                        isSearchable={false}
                        isClearable={false}
                        labelWidth={labelWidth}
                        layout={layout}
                    />
                    {validationErrors.assertionStatusError}
                </>}

            {condition.triggerType === TriggerTypes.USER_INPUT &&
                <>
                    <SelectFormGroup
                        automationId={`creation-policy-component-trigger-configuration-attribute-type-select-${index}`}
                        controlId={`creation-policy-component-trigger-configuration-attribute-type-select-${index}`}
                        label={triggerConfigurationResources.inputs.attribute.label}
                        ariaLabel={triggerConfigurationResources.inputs.attribute.label}
                        onChange={handlePolicyComponentAttributeTypeChange}
                        options={attributeTypeOptions}
                        selectedOption={selectedAttributeTypeOption}
                        disabled={isDisabled}
                        isRequired={true}
                        isSearchable={true}
                        isClearable={false}
                        labelWidth={labelWidth}
                        layout={layout}
                    />
                    <SelectFormGroup
                        automationId={`creation-policy-component-trigger-configuration-attribute-field-select-${index}`}
                        controlId={`creation-policy-component-trigger-configuration-attribute-field-select-${index}`}
                        label={triggerConfigurationResources.inputs.attributeField.label}
                        ariaLabel={triggerConfigurationResources.inputs.attributeField.label}
                        onChange={handlePolicyComponentAttributeFieldChange}
                        options={attributeFieldOptions}
                        selectedOption={selectedAttributeFieldOption}
                        disabled={isDisabled || !condition.attributeTypeID}
                        isRequired={true}
                        isSearchable={true}
                        isClearable={false}
                        labelWidth={labelWidth}
                        layout={layout}
                    />
                    {validationErrors.attributeFieldError}
                </>}

            {condition.triggerType === TriggerTypes.ACQUIRED_DATA &&
                <>
                    <SelectFormGroup
                        automationId={`creation-policy-component-trigger-configuration-so-select-${index}`}
                        controlId={`creation-policy-component-trigger-configuration-so-select-${index}`}
                        label={triggerConfigurationResources.inputs.serviceOffering.label}
                        ariaLabel={triggerConfigurationResources.inputs.serviceOffering.label}
                        onChange={handlePolicyComponentServiceOfferingChange}
                        options={serviceOfferingOptions}
                        selectedOption={selectedServiceOfferingOption}
                        disabled={isDisabled}
                        isRequired={true}
                        isSearchable={true}
                        isClearable={false}
                        labelWidth={labelWidth}
                        layout={layout}
                    />
                    {validationErrors.serviceOfferingError}
                    <SelectFormGroup
                        automationId={`creation-policy-component-trigger-configuration-acquired-attribute-type-select-${index}`}
                        controlId={`creation-policy-component-trigger-configuration-acquired-attribute-type-select-${index}`}
                        label={triggerConfigurationResources.inputs.acquiredAttribute.label}
                        ariaLabel={triggerConfigurationResources.inputs.acquiredAttribute.label}
                        onChange={handlePolicyComponentAcquiredAttributeTypeChange}
                        options={acquiredAttributeTypeOptions}
                        selectedOption={selectedAttributeTypeOption}
                        disabled={isDisabled || !condition.serviceOfferingID}
                        isRequired={true}
                        isSearchable={true}
                        isClearable={false}
                        labelWidth={labelWidth}
                        layout={layout}
                    />
                    <SelectFormGroup
                        automationId={`creation-policy-component-trigger-configuration-acquired-attribute-field-select-${index}`}
                        controlId={`creation-policy-component-trigger-configuration-acquired-attribute-field-select-${index}`}
                        label={triggerConfigurationResources.inputs.attributeField.label}
                        ariaLabel={triggerConfigurationResources.inputs.attributeField.label}
                        onChange={handlePolicyComponentAcquiredAttributeFieldChange}
                        options={acquiredAttributeFieldOptions}
                        selectedOption={selectedAttributeFieldOption}
                        disabled={isDisabled || !condition.attributeTypeID || !condition.serviceOfferingID}
                        isRequired={true}
                        isSearchable={true}
                        isClearable={false}
                        labelWidth={labelWidth}
                        layout={layout}
                    />
                    {validationErrors.attributeFieldError}

                </>}

            {condition.triggerType === TriggerTypes.COMPONENT &&
                <>
                    <SelectFormGroup
                        automationId={`creation-policy-component-trigger-component-select-${index}`}
                        controlId={`creation-policy-component-trigger-component-select-${index}`}
                        label={triggerConfigurationResources.inputs.component.label}
                        ariaLabel={triggerConfigurationResources.inputs.component.label}
                        onChange={handlePolicyComponentChange}
                        options={policyComponentOptions}
                        selectedOption={selectedPolicyComponentOption}
                        disabled={isDisabled}
                        isRequired={true}
                        isSearchable={true}
                        isClearable={false}
                        labelWidth={labelWidth}
                        layout={layout}
                    />
                    {validationErrors.policyComponentError}

                    <SelectFormGroup
                        automationId={`creation-policy-component-trigger-configuration-status-select-${index}`}
                        controlId={`creation-policy-component-trigger-configuration-status-select-${index}`}
                        label={triggerConfigurationResources.inputs.assertionStatus.label}
                        ariaLabel={triggerConfigurationResources.inputs.assertionStatus.label}
                        onChange={handlePolicyComponentStatusChange}
                        options={POLICY_CONFIG_DECISION_COMPONENT_RESULT_OPTIONS}
                        selectedOption={selectedPolicyComponentStatusOption}
                        disabled={isDisabled}
                        isRequired={true}
                        isSearchable={false}
                        isClearable={false}
                        labelWidth={labelWidth}
                        layout={layout}
                    />
                    {validationErrors.policyComponentStatusError}

                </>
            }

            {(condition.triggerType === TriggerTypes.USER_INPUT || condition.triggerType === TriggerTypes.ACQUIRED_DATA) &&
                <>
                    <SelectFormGroup
                        automationId={`creation-policy-component-trigger-configuration-comparison-select-${index}`}
                        controlId={`creation-policy-component-trigger-configuration-comparison-select-${index}`}
                        label={triggerConfigurationResources.inputs.comparison.label}
                        ariaLabel={triggerConfigurationResources.inputs.comparison.label}
                        onChange={handleComparisonChange}
                        options={POLICY_CONFIG_DECISION_ATTRIBUTE_FIELD_RESULT_OPTIONS}
                        selectedOption={selectedComparisonOption}
                        disabled={isDisabled}
                        isRequired={true}
                        isSearchable={false}
                        isClearable={false}
                        labelWidth={labelWidth}
                        layout={layout}
                    />

                    {validationErrors.comparisonError}


                    <InputFormGroup
                        automationId={`creation-policy-component-trigger-configuration-value-select-${index}`}
                        controlId={`creation-policy-component-trigger-configuration-value-select-${index}`}
                        label={triggerConfigurationResources.inputs.value.label}
                        ariaLabel={triggerConfigurationResources.inputs.value.label}
                        onChange={handlePolicyComponentValueChange}
                        value={condition.values || ""}
                        disabled={isDisabled}
                        isRequired={true}
                        labelWidth={labelWidth}
                        layout={layout}
                    />

                    {validationErrors.policyComponentValueError}
                </>
            }

            {!isDisabled && <div className={styles.optionContainer}>
                <Button
                    id={`creation-policy-component-trigger-configuration-remove-${index}`}
                    variant="primary"
                    onClick={removeTrigger}
                >
                    {"Remove"}
                </Button>
            </div>}
        </div>
    );
}